import { isHotkey } from "is-hotkey";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "ds/components/Box";
import useTypedContext from "hooks/useTypedContext";

import { RunElementsContext } from "../../RunElementsContext";
import { RunShortcuts } from "../../types";
import NavigationBarAction from "./Action";
import styles from "./styles.module.css";

type NavigationBarProps = {
  next?: string;
  previous?: string;
};

const NavigationBar = ({ next, previous }: NavigationBarProps) => {
  const navigate = useNavigate();
  const { stackId } = useParams<{ stackId: string }>();
  const { shortcuts } = useTypedContext(RunElementsContext);

  const handleNavigate = useCallback(
    (runId?: string) => () => {
      if (!runId) {
        return;
      }

      navigate(`/stack/${stackId}/run/${runId}`);
    },
    [navigate, stackId]
  );

  const navShortcuts = useMemo<RunShortcuts>(() => {
    return [
      {
        id: "previous",
        label: "Previous run",
        callback: handleNavigate(previous),
        code: "left",
        disabled: !previous,
      },
      {
        id: "next",
        label: "Next run",
        callback: handleNavigate(next),
        code: "right",
        disabled: !next,
      },
    ];
  }, [next, previous, handleNavigate]);

  const actionShortcuts = useMemo(() => {
    return shortcuts
      .filter((item) => !item.disabled)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [shortcuts]);

  const allShortcuts = useMemo(() => {
    return [...actionShortcuts, ...navShortcuts];
  }, [actionShortcuts, navShortcuts]);

  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      const { tagName } = e.target as HTMLElement;
      const isTargetValid = tagName !== "TEXTAREA" && tagName !== "INPUT";

      if (!isTargetValid) {
        return;
      }

      const shortcut = allShortcuts.find((item) => isHotkey(item.code, e));

      if (shortcut && !shortcut.disabled && !shortcut.loading) {
        e.preventDefault(); // Prevent default behavior for all shortcuts
        shortcut.callback();
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [allShortcuts]);

  return (
    <Box className={styles.wrapper}>
      <Box padding="0 medium" gap="x-large">
        {navShortcuts.map((action) => (
          <NavigationBarAction key={action.id} {...action} />
        ))}
      </Box>
      {actionShortcuts.length > 0 && (
        <Box className={styles.actionsWrapper} padding="0 medium" gap="x-large">
          {actionShortcuts.map((action) => (
            <NavigationBarAction key={action.id} {...action} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default NavigationBar;
